import { useEffect, useMemo, useState } from 'react';
import { Button } from '@jeraisonne/ui';
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Loader2,
  Plus,
  Save,
  Search,
  Trash2,
} from 'lucide-react';
import type { SchoolLevel } from '@jeraisonne/db';
import {
  DEMO_LEVELS,
  LEVEL_LABEL,
  MAX_WARMUP_QUESTIONS,
  MAX_EXERCISE_STEPS,
} from '@/features/demo/types';
import {
  listDemoConfigs,
  listFundamentalExercises,
  listPublishedExercises,
  upsertDemoConfig,
  type DemoConfigRow,
  type ExerciseRow,
} from '@/lib/admin/demo-config';

/**
 * Page admin `/tutor/admin/demo` : configure le contenu présenté dans la
 * démo anonyme pour chaque niveau. La démo retombe sur des données locales
 * si aucune config active n'existe → cette page est optionnelle.
 */
export function TutorAdminDemoPage() {
  const [configs, setConfigs] = useState<DemoConfigRow[]>([]);
  const [activeLevel, setActiveLevel] = useState<SchoolLevel>('3e');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const c = await listDemoConfigs();
      setConfigs(c);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  const currentConfig = useMemo(
    () => configs.find((c) => c.level === activeLevel) ?? null,
    [configs, activeLevel],
  );

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:py-10">
      <header className="mb-6 space-y-1">
        <p className="text-primary-600 text-xs font-bold uppercase tracking-wider">
          Admin · Démo anonyme
        </p>
        <h1 className="text-ink-900 font-serif text-2xl font-semibold sm:text-3xl">
          Contenu de la démo
        </h1>
        <p className="text-ink-500 text-sm">
          Configure les exercices présentés sur{' '}
          <code className="bg-ink-100 rounded px-1">/demo</code> (parcours guidé anonyme). Pas de
          config active = la démo tombe sur des données locales par défaut.
        </p>
      </header>

      {/* Onglets niveaux */}
      <div className="border-ink-100 mb-6 flex flex-wrap gap-1 border-b">
        {DEMO_LEVELS.map((level) => {
          const cfg = configs.find((c) => c.level === level);
          const isActiveTab = activeLevel === level;
          return (
            <button
              key={level}
              type="button"
              onClick={() => setActiveLevel(level)}
              className={
                'relative px-3 py-2 text-sm font-semibold transition-colors ' +
                (isActiveTab
                  ? 'text-primary-700 border-primary-600 -mb-px border-b-2'
                  : 'text-ink-500 hover:text-ink-900')
              }
            >
              {LEVEL_LABEL[level]}
              {cfg?.is_active && (
                <span className="bg-xp-500 ml-1.5 inline-block h-1.5 w-1.5 rounded-full align-middle" />
              )}
            </button>
          );
        })}
      </div>

      {error && (
        <div className="bg-error-50 border-error-100 mb-4 rounded-xl border px-4 py-3 text-sm">
          <p className="text-error-700">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="text-ink-500 flex items-center gap-2 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
        </div>
      ) : (
        <DemoConfigEditor
          key={activeLevel + (currentConfig?.id ?? 'new')}
          level={activeLevel}
          initialConfig={currentConfig}
          onSaved={refresh}
        />
      )}
    </div>
  );
}

// ── Éditeur de config pour un niveau ─────────────────────────────────────────

function DemoConfigEditor({
  level,
  initialConfig,
  onSaved,
}: {
  level: SchoolLevel;
  initialConfig: DemoConfigRow | null;
  onSaved: () => void;
}) {
  const [warmupIds, setWarmupIds] = useState<string[]>(initialConfig?.warmup_exercise_ids ?? []);
  const [mainId, setMainId] = useState<string | null>(initialConfig?.main_exercise_id ?? null);
  const [isActive, setIsActive] = useState<boolean>(initialConfig?.is_active ?? false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const [fundamentalExos, setFundamentalExos] = useState<ExerciseRow[]>([]);
  const [publishedExos, setPublishedExos] = useState<ExerciseRow[]>([]);
  const [exosLoading, setExosLoading] = useState(true);
  const [exosError, setExosError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setExosLoading(true);
    setExosError(null);
    Promise.all([listFundamentalExercises(level), listPublishedExercises(level)])
      .then(([f, p]) => {
        if (cancelled) return;
        setFundamentalExos(f);
        setPublishedExos(p);
        setExosLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setExosError(e instanceof Error ? e.message : 'Erreur de chargement des exercices.');
        setExosLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [level]);

  const mainExercise = useMemo(
    () => publishedExos.find((e) => e.id === mainId) ?? null,
    [publishedExos, mainId],
  );

  const warmupItems = useMemo(() => {
    return warmupIds.map((id) => ({
      id,
      exercise: fundamentalExos.find((e) => e.id === id) ?? null,
    }));
  }, [warmupIds, fundamentalExos]);

  const warnings = computeWarnings({
    warmupIds,
    warmupItems,
    mainExercise,
    mainId,
  });

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      await upsertDemoConfig({
        id: initialConfig?.id,
        level,
        warmup_exercise_ids: warmupIds,
        main_exercise_id: mainId,
        is_active: isActive,
      });
      setSaved(true);
      onSaved();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Échec de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="border-ink-100 rounded-2xl border bg-white p-4 sm:p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-ink-900 font-semibold">État de la configuration</h2>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4"
            />
            Active pour {LEVEL_LABEL[level]}
          </label>
        </div>
        <p className="text-ink-500 text-xs">
          1 seule config peut être active par niveau. Activer celle-ci désactivera automatiquement
          les autres pour {LEVEL_LABEL[level]}.
        </p>
      </div>

      {/* Warnings */}
      {warnings.length > 0 && (
        <ul className="space-y-1 rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm">
          {warnings.map((w) => (
            <li key={w} className="flex items-start gap-2 text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              {w}
            </li>
          ))}
        </ul>
      )}

      {exosError && (
        <div className="bg-error-50 border-error-100 rounded-xl border px-4 py-3 text-sm">
          <p className="text-error-700">{exosError}</p>
        </div>
      )}

      {/* Warmup */}
      <section className="border-ink-100 rounded-2xl border bg-white p-4 sm:p-5">
        <header className="mb-3">
          <h2 className="text-ink-900 font-semibold">
            Échauffement · {warmupIds.length} / {MAX_WARMUP_QUESTIONS}
          </h2>
          <p className="text-ink-500 text-xs">
            La première étape (step) de chacun de ces exercices fondamentaux sert de question
            d&apos;échauffement. La démo consomme au maximum {MAX_WARMUP_QUESTIONS} questions.
          </p>
        </header>

        {warmupItems.length === 0 ? (
          <p className="text-ink-400 text-sm">Aucun exercice ajouté.</p>
        ) : (
          <ul className="space-y-2">
            {warmupItems.map((item, idx) => (
              <li
                key={item.id}
                className="border-ink-100 flex items-center justify-between gap-3 rounded-xl border px-3 py-2"
              >
                <span className="text-ink-400 w-6 text-xs font-bold">{idx + 1}.</span>
                <div className="min-w-0 flex-1">
                  {item.exercise ? (
                    <>
                      <p className="text-ink-900 truncate text-sm">{item.exercise.title}</p>
                      <p className="text-ink-400 text-xs">
                        {item.exercise.chapter_title ?? '— sans chapitre —'}
                      </p>
                    </>
                  ) : (
                    <p className="text-error-600 text-sm">
                      Exercice introuvable ou non éligible (id: {item.id.slice(0, 8)}…)
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setWarmupIds((prev) => prev.filter((_, i) => i !== idx))}
                  className="text-ink-400 hover:text-error-600 p-1.5"
                  aria-label="Retirer"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {!exosLoading && (
          <ExercisePicker
            label="Ajouter un exercice fondamental"
            exercises={fundamentalExos.filter((e) => !warmupIds.includes(e.id))}
            onPick={(id) => setWarmupIds((prev) => [...prev, id])}
            disabled={warmupIds.length >= MAX_WARMUP_QUESTIONS}
          />
        )}
      </section>

      {/* Exercice principal */}
      <section className="border-ink-100 rounded-2xl border bg-white p-4 sm:p-5">
        <header className="mb-3">
          <h2 className="text-ink-900 font-semibold">Exercice guidé principal</h2>
          <p className="text-ink-500 text-xs">
            Présenté étape par étape (max {MAX_EXERCISE_STEPS} étapes consommées). Doit être publié.
          </p>
        </header>

        {mainExercise ? (
          <div className="border-ink-100 mb-3 flex items-center justify-between gap-3 rounded-xl border px-3 py-2">
            <div className="min-w-0 flex-1">
              <p className="text-ink-900 truncate text-sm">{mainExercise.title}</p>
              <p className="text-ink-400 text-xs">
                {mainExercise.chapter_title ?? '— sans chapitre —'} ·{' '}
                {mainExercise.is_fundamental ? 'Fondamental' : 'Guidé'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setMainId(null)}
              className="text-ink-400 hover:text-error-600 p-1.5"
              aria-label="Retirer"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ) : mainId ? (
          <p className="text-error-600 mb-3 text-sm">
            Exercice {mainId.slice(0, 8)}… introuvable ou non publié pour ce niveau.
          </p>
        ) : (
          <p className="text-ink-400 mb-3 text-sm">Aucun exercice sélectionné.</p>
        )}

        {!exosLoading && (
          <ExercisePicker
            label="Choisir un exercice publié"
            exercises={publishedExos}
            onPick={(id) => setMainId(id)}
            disabled={false}
          />
        )}
      </section>

      {/* Actions */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3 text-sm">
          {saved && (
            <span className="text-xp-700 inline-flex items-center gap-1">
              <CheckCircle2 className="h-4 w-4" /> Enregistré.
            </span>
          )}
          {saveError && <span className="text-error-700">{saveError}</span>}
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <a href="/demo" target="_blank" rel="noreferrer">
            <Button variant="outline" size="md" className="flex items-center gap-2">
              <ExternalLink className="h-4 w-4" />
              Aperçu /demo
            </Button>
          </a>
          <Button
            onClick={handleSave}
            disabled={saving}
            size="md"
            className="flex items-center gap-2"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
}

function computeWarnings(args: {
  warmupIds: string[];
  warmupItems: { id: string; exercise: ExerciseRow | null }[];
  mainExercise: ExerciseRow | null;
  mainId: string | null;
}): string[] {
  const warnings: string[] = [];
  if (args.warmupIds.length < MAX_WARMUP_QUESTIONS) {
    warnings.push(
      `Le warmup contient ${args.warmupIds.length} exercice(s) — il en faut ${MAX_WARMUP_QUESTIONS} pour activer cette config (sinon fallback local).`,
    );
  }
  const missing = args.warmupItems.filter((i) => !i.exercise);
  if (missing.length > 0) {
    warnings.push(`${missing.length} exercice(s) du warmup introuvable(s) ou non publié(s).`);
  }
  if (!args.mainExercise && args.mainId) {
    warnings.push('L’exercice principal sélectionné n’est pas publié pour ce niveau.');
  }
  if (!args.mainId) {
    warnings.push(
      'Aucun exercice principal sélectionné (la démo basculera sur le fallback local).',
    );
  }
  return warnings;
}

function ExercisePicker({
  label,
  exercises,
  onPick,
  disabled,
}: {
  label: string;
  exercises: ExerciseRow[];
  onPick: (id: string) => void;
  disabled: boolean;
}) {
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return exercises.slice(0, 50);
    return exercises
      .filter(
        (e) =>
          e.title.toLowerCase().includes(q) ||
          (e.chapter_title?.toLowerCase().includes(q) ?? false),
      )
      .slice(0, 50);
  }, [exercises, query]);

  return (
    <div className="mt-3">
      <label className="text-ink-400 mb-1.5 block text-xs font-semibold uppercase tracking-wide">
        {label}
      </label>
      <div className="border-ink-200 flex items-center gap-2 rounded-xl border bg-white px-3 py-2">
        <Search className="text-ink-400 h-4 w-4" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher un titre ou un chapitre"
          className="flex-1 bg-transparent text-sm outline-none"
        />
      </div>
      {filtered.length === 0 ? (
        <p className="text-ink-400 mt-2 text-sm">Aucun résultat.</p>
      ) : (
        <ul className="border-ink-100 mt-2 max-h-64 overflow-y-auto rounded-xl border bg-white">
          {filtered.map((e) => (
            <li key={e.id} className="border-ink-100 border-b last:border-b-0">
              <button
                type="button"
                disabled={disabled}
                onClick={() => onPick(e.id)}
                className="hover:bg-ink-50 flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className="min-w-0 flex-1">
                  <span className="text-ink-900 block truncate">{e.title}</span>
                  <span className="text-ink-400 block truncate text-xs">
                    {e.chapter_title ?? '— sans chapitre —'} · {e.chapter_level ?? '?'}
                  </span>
                </span>
                <Plus className="text-primary-600 h-4 w-4 shrink-0" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
